import { type ReactNode } from 'react';
import { brandLock } from '@icebreaker/shared/design';
import { BrandMark, type BrandMarkTone } from './brand-mark';
import { type UiLinkComponent } from './link-slot';
import styles from './brand-lock.module.css';

/**
 * MARKA KİLİDİ — işaret + wordmark (marka sözleşmesi §4, §7).
 *
 * Ekranların marka bloğu TEK yerden çizilir: üst bant, alt bilgi, giriş, hero,
 * panel kromu ve hata iskeleti aynı bileşeni farklı ölçülerle çağırır. Ölçüler
 * §7'nin yüzey tablosundan gelir ve ÇAĞIRAN verir — bileşen kendi ölçüsünü
 * uydurmaz.
 *
 * Marka adı `brandName` ile ÇAĞIRANDAN gelir (token katmanı); hiçbir ekrana
 * yazılmaz. `linkComponent` yönlendirici sınırıdır — bkz. `link-slot.ts`.
 */
export interface BrandLockBaseProps {
  /** İşaret karesinin kenarı (px) — §7 tablosundaki değer. */
  readonly square: number;
  /** Wordmark punto (px) — §7 tablosundaki değer. Verilmezse wordmark çizilmez. */
  readonly wordmark?: number;
  readonly tone?: BrandMarkTone;
  /** Dikey kilit yalnız splash / ikon altı / kare alanlarda (§4). */
  readonly orientation?: 'horizontal' | 'vertical';
  /** Verilirse kilit bir bağlantı olur (üst bant, alt bilgi). */
  readonly href?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
  /** Wordmark'tan sonra gelen rol eki ("Organizatör", "Admin"). */
  readonly suffix?: ReactNode;
  /** Marka katmanından gelen ürün adı — bileşene SABİTLENMEZ. */
  readonly brandName: string;
  /** `href` verildiğinde çizilecek bağlantı bileşeni (uygulama enjekte eder). */
  readonly linkComponent: UiLinkComponent;
}

export function BrandLockBase({
  square,
  wordmark,
  tone = 'surface',
  orientation = 'horizontal',
  href,
  ariaLabel,
  className,
  suffix,
  brandName,
  linkComponent: Link,
}: BrandLockBaseProps) {
  const style = {
    '--lock-gap': `${Math.round(square * brandLock.gapRatio)}px`,
    '--lock-wordmark': wordmark === undefined ? undefined : `${wordmark}px`,
  } as React.CSSProperties;

  const content = (
    <>
      <BrandMark size={square} tone={tone} />
      {/*
        `data-brand-wordmark`: wordmark'ı KUŞATAN yüzeyler (üst bant) onu kendi
        kırılım merdiveninde ele alabilsin diye adlandırılmış kanca. Modül
        sınıfı karma olduğu için dışarıdan hedeflenemez; ölçü/gizleme kararı
        ÇAĞIRANINDIR (§7 yüzey tablosu), bileşenin değil.
      */}
      {wordmark === undefined ? null : (
        <span className={styles.wordmark} data-brand-wordmark="">
          {brandName}
        </span>
      )}
      {suffix}
    </>
  );

  const classes = [
    styles.lock,
    orientation === 'vertical' ? styles.vertical : '',
    href ? styles.link : '',
    href && tone === 'band' ? styles.onBand : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (href) {
    return (
      <Link className={classes} style={style} href={href} aria-label={ariaLabel}>
        {content}
      </Link>
    );
  }
  return (
    <span className={classes} style={style}>
      {content}
    </span>
  );
}
